import {
  BRAND_SCOPE_LABELS,
  COMPANY_LABELS,
  type AreaDto,
  type BrandProfileDto,
  type BrandScope,
  type ChannelDto,
  type Company,
  type ResolvedBrandDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, Globe, Layers, Package, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { api, query } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { DocumentList } from './DocumentList.js';
import { TagInput } from './TagInput.js';

/** Which level is being edited. Mirrors the inheritance, top to bottom. */
export interface Level {
  scope: BrandScope;
  company?: Company;
  areaId?: string;
  channelId?: string;
  label: string;
}

const COMPANY_LEVELS: Level[] = [
  { scope: 'COMPANY', company: 'SPAMA', label: COMPANY_LABELS.SPAMA },
  { scope: 'COMPANY', company: 'PROKOM', label: COMPANY_LABELS.PROKOM },
  { scope: 'COMPANY', company: 'BEGGE', label: COMPANY_LABELS.BEGGE },
];

/**
 * Tone of voice and profile material, per level (07-utviklingsplan.md 2.1.2).
 *
 * The screen is built around the inheritance rather than around the tables: pick
 * a level on the left, edit it on the right, and see underneath exactly what the
 * agents end up being told once the levels are merged. Without that last part it
 * is guesswork whether a change had any effect at all.
 */
export function BrandAdmin() {
  const [level, setLevel] = useState<Level>({ scope: 'GLOBAL', label: BRAND_SCOPE_LABELS.GLOBAL });

  const { data: areas = [], isLoading } = useQuery({
    queryKey: ['areas', 'admin'],
    queryFn: () => api.get<AreaDto[]>('/areas?includeInactive=true'),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<ChannelDto[]>('/channels'),
  });

  if (isLoading) return <SkeletonPanel lines={8} />;

  return (
    <div className="grid gap-3 lg:grid-cols-[240px_1fr]">
      <nav aria-label="Profilnivåer" className="space-y-3">
        <LevelGroup
          title="Felles"
          Icon={Globe}
          levels={[{ scope: 'GLOBAL', label: BRAND_SCOPE_LABELS.GLOBAL }]}
          active={level}
          onPick={setLevel}
        />
        <LevelGroup
          title="Selskap"
          Icon={Building2}
          levels={COMPANY_LEVELS}
          active={level}
          onPick={setLevel}
        />
        <LevelGroup
          title="Områder"
          Icon={Package}
          levels={areas.map((area) => ({
            scope: 'AREA' as const,
            areaId: area.id,
            label: area.name,
          }))}
          active={level}
          onPick={setLevel}
        />
        <LevelGroup
          title="Kanaler"
          Icon={Send}
          levels={channels.map((channel) => ({
            scope: 'CHANNEL' as const,
            channelId: channel.id,
            label: channel.name,
          }))}
          active={level}
          onPick={setLevel}
        />
      </nav>

      <div className="min-w-0 space-y-3">
        <ProfileEditor key={levelKey(level)} level={level} />
        {/* A channel carries tone only — no documents, and it layers on top of
            an area, so the merged preview belongs to the area view. */}
        {level.scope !== 'CHANNEL' && <DocumentList level={level} />}
        {level.scope === 'AREA' && level.areaId && <ResolvedPreview areaId={level.areaId} />}
        {level.scope !== 'AREA' && level.scope !== 'CHANNEL' && (
          <ResolvedPreview areaId={undefined} />
        )}
      </div>
    </div>
  );
}

function levelKey(level: Level): string {
  return `${level.scope}:${level.company ?? ''}:${level.areaId ?? ''}:${level.channelId ?? ''}`;
}

function LevelGroup({
  title,
  Icon,
  levels,
  active,
  onPick,
}: {
  title: string;
  Icon: typeof Globe;
  levels: Level[];
  active: Level;
  onPick: (level: Level) => void;
}) {
  return (
    <div>
      <p className="label-micro mb-1.5 flex items-center gap-1.5 px-1">
        <Icon size={11} aria-hidden />
        {title}
      </p>
      <div className="space-y-0.5">
        {levels.map((level) => (
          <button
            key={levelKey(level)}
            type="button"
            onClick={() => onPick(level)}
            aria-current={levelKey(level) === levelKey(active)}
            className={cn(
              'w-full truncate rounded-lg px-2.5 py-1.5 text-left text-xs transition',
              levelKey(level) === levelKey(active)
                ? 'bg-accent/15 text-accent'
                : 'text-ink-soft hover:text-ink hover:bg-raised/60',
            )}
          >
            {level.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ProfileEditor({ level }: { level: Level }) {
  const queryClient = useQueryClient();
  const params = {
    scope: level.scope,
    company: level.company,
    areaId: level.areaId,
    channelId: level.channelId,
  };

  const { data: profile, isLoading } = useQuery({
    queryKey: ['brand-profile', levelKey(level)],
    queryFn: () => api.get<BrandProfileDto | null>(`/brand/profile${query(params)}`),
  });

  const [form, setForm] = useState({
    toneOfVoice: '',
    positioning: '',
    wordsWeUse: [] as string[],
    wordsWeAvoid: [] as string[],
    goodExamples: '',
    badExamples: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setForm({
      toneOfVoice: profile.toneOfVoice ?? '',
      positioning: profile.positioning ?? '',
      wordsWeUse: profile.wordsWeUse,
      wordsWeAvoid: profile.wordsWeAvoid,
      goodExamples: profile.goodExamples ?? '',
      badExamples: profile.badExamples ?? '',
    });
  }, [profile]);

  const save = useMutation({
    mutationFn: () =>
      api.put<BrandProfileDto>(`/brand/profile${query(params)}`, {
        toneOfVoice: form.toneOfVoice || null,
        positioning: form.positioning || null,
        wordsWeUse: form.wordsWeUse,
        wordsWeAvoid: form.wordsWeAvoid,
        goodExamples: form.goodExamples || null,
        badExamples: form.badExamples || null,
      }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['brand-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['brand-resolved'] });
    },
  });

  if (isLoading) return <SkeletonPanel lines={8} />;

  return (
    <GlassPanel>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-display text-ink text-base">{level.label}</h2>
          <p className="text-ink-soft mt-0.5 text-[11px]">
            {level.scope === 'GLOBAL'
              ? 'Gjelder alt Nova skriver, i begge selskaper.'
              : level.scope === 'CHANNEL'
                ? 'Kanalens egen stemme. Legges til på toppen når innhold tilpasses denne kanalen.'
                : 'Legges til nivået over — tomme felter arves.'}
          </p>
        </div>
        {profile?.updatedBy && (
          <p className="text-ink-soft/70 text-[11px]">Sist endret av {profile.updatedBy.name}</p>
        )}
      </div>

      <div className="mt-4 space-y-4">
        <Field
          label="Posisjonering og verdiløfte"
          hint="Hva vi lover, i én til tre setninger."
          value={form.positioning}
          onChange={(positioning) => setForm({ ...form, positioning })}
          rows={3}
        />

        <Field
          label="Tone of voice"
          hint="Hvordan vi skriver. Dette er teksten agentene får ordrett."
          value={form.toneOfVoice}
          onChange={(toneOfVoice) => setForm({ ...form, toneOfVoice })}
          rows={7}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="label-micro mb-1.5 block" htmlFor="words-use">
              Ord vi bruker
            </label>
            <TagInput
              id="words-use"
              value={form.wordsWeUse}
              onChange={(wordsWeUse) => setForm({ ...form, wordsWeUse })}
              placeholder="Skriv et ord og trykk enter"
            />
          </div>
          <div>
            <label className="label-micro mb-1.5 block" htmlFor="words-avoid">
              Ord vi ikke bruker
            </label>
            <TagInput
              id="words-avoid"
              tone="danger"
              value={form.wordsWeAvoid}
              onChange={(wordsWeAvoid) => setForm({ ...form, wordsWeAvoid })}
              placeholder="Skriv et ord og trykk enter"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="Slik treffer det"
            hint="Et eksempel som er slik vi vil ha det, og hvorfor."
            value={form.goodExamples}
            onChange={(goodExamples) => setForm({ ...form, goodExamples })}
            rows={5}
          />
          <Field
            label="Slik treffer det ikke"
            hint="Et eksempel som bommer, og hvorfor."
            value={form.badExamples}
            onChange={(badExamples) => setForm({ ...form, badExamples })}
            rows={5}
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Lagre profil
          </Button>
          {saved && (
            <span className="text-success inline-flex items-center gap-1 text-xs">
              <Check size={13} aria-hidden />
              Lagret — gjelder fra neste agentkjøring
            </span>
          )}
          {save.error && <span className="text-danger text-xs">{save.error.message}</span>}
        </div>
      </div>
    </GlassPanel>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  rows,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div>
      <label className="label-micro mb-1.5 block" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        spellCheck
        lang="nb"
        placeholder={hint}
        className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full resize-y rounded-lg border p-3 text-xs leading-relaxed focus:outline-none"
      />
    </div>
  );
}

/** The merged result, so nobody has to guess what the agents actually receive. */
export function ResolvedPreview({ areaId }: { areaId?: string }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['brand-resolved', areaId ?? 'global'],
    queryFn: () => api.get<ResolvedBrandDto>(`/brand/resolved${query({ areaId })}`),
  });

  return (
    <GlassPanel>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-ink-soft" aria-hidden />
          <h2 className="font-display text-ink text-base">Dette får agentene</h2>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-ink-soft hover:text-ink text-xs transition"
        >
          {open ? 'Skjul' : 'Vis'}
        </button>
      </div>

      <p className="text-ink-soft mt-1 text-[11px]">
        {areaId
          ? 'Felles, selskap og område slått sammen, i den rekkefølgen.'
          : 'Fellesnivået alene. Velg et område for å se det sammenslåtte resultatet.'}
      </p>

      {open && (
        <pre className="border-hairline bg-base/40 text-ink-soft mt-3 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-lg border p-3 text-[11px] leading-relaxed">
          {data?.prompt ?? 'Henter …'}
        </pre>
      )}
    </GlassPanel>
  );
}
